import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { SEMINAR } from "@/seminars";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = `Admin · ${SEMINAR.siteName}`;
  }, []);

  if (user) return <Navigate to="/dashboard/home" replace />;

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard/home");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark admin-shell flex min-h-screen items-center justify-center px-4 text-foreground" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.2, 0.75, 0.25, 1] }} className="admin-card relative w-full max-w-md p-8 sm:p-10">
        <div className="mb-8 flex items-center">
          <img src="/affinity-logo.png" alt="Affinity, A Group Representing Infinity FA" className="h-20 w-auto drop-shadow-lg" data-testid="admin-login-logo" />
        </div>
        <p className="admin-kicker mb-2">Admin access</p>
        <h1 className="mb-6 text-2xl font-bold tracking-tight sm:text-3xl">Sign in to manage registrations</h1>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="adminEmail">Email</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input id="adminEmail" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl pl-10" placeholder="admin@affinitygroup.sg" data-testid="admin-email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="adminPassword">Password</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input id="adminPassword" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl pl-10" placeholder="••••••••" data-testid="admin-password-input" />
            </div>
          </div>
          {error && (
            <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-200" role="alert" data-testid="admin-login-error">{error}</p>
          )}
          <Button type="submit" disabled={loading} className="admin-gold-btn h-12 w-full rounded-full text-sm font-bold transition-transform" data-testid="admin-login-btn">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
